import { ShoppingCart } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useCart } from "@/contexts/CartContext";
import { CartItem } from "./CartItem";
import { formatPrice } from "@/utils/format";
import { useIsMobile } from "@/hooks/use-mobile";

function CartContent({ onClose, isMobile }: { onClose: () => void; isMobile?: boolean }) {
  const { items, itemCount, subtotal, updateQuantity, removeItem } = useCart();

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-8">
        <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
          <ShoppingCart className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="font-semibold text-lg mb-2">Tu carrito está vacío</h3>
        <p className="text-muted-foreground text-sm mb-6">
          ¡Agregá productos para comenzar tu compra!
        </p>
        <Button asChild onClick={onClose}>
          <Link to="/catalogo">Ver Catálogo</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {isMobile ? (
        <div className="flex-1 overflow-y-auto -mx-6 px-6 max-h-[calc(70vh-180px)]">
          <div className="py-2">
            {items.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={updateQuantity}
                onRemove={removeItem}
              />
            ))}
          </div>
        </div>
      ) : (
        <ScrollArea className="flex-1 -mx-6 px-6 min-h-0">
          <div className="py-2">
            {items.map((item) => (
              <CartItem
                key={item.id}
                item={item}
                onUpdateQuantity={updateQuantity}
                onRemove={removeItem}
              />
            ))}
          </div>
        </ScrollArea>
      )}

      <div className="pt-4 space-y-4 flex-shrink-0">
        <Separator />
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="text-xl font-bold">{formatPrice(subtotal)}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Envío calculado en el checkout
        </p>
        <div className="grid gap-2">
          <Button size="lg" className="w-full" asChild onClick={onClose}>
            <Link to="/checkout">Continuar al Checkout</Link>
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={onClose}
          >
            Seguir Comprando
          </Button>
        </div>
      </div>
    </div>
  );
}

function CartHeader({ itemCount }: { itemCount: number }) {
  return (
    <div className="flex items-center gap-2">
      <ShoppingCart className="h-5 w-5" />
      Tu Carrito
      {itemCount > 0 && (
        <span className="text-sm font-normal text-muted-foreground">
          ({itemCount} {itemCount === 1 ? "producto" : "productos"})
        </span>
      )}
    </div>
  );
}

export function CartDrawer() {
  const { isOpen, setIsOpen, itemCount } = useCart();
  const isMobile = useIsMobile();

  const handleClose = () => setIsOpen(false);

  // Mobile: Bottom drawer with swipe gesture
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent className="max-h-[85vh] flex flex-col">
          <DrawerHeader className="text-left">
            <DrawerTitle>
              <CartHeader itemCount={itemCount} />
            </DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 flex flex-col px-4 pb-4 min-h-0 overflow-hidden">
            <CartContent onClose={handleClose} isMobile />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Side sheet
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent side="right" className="flex flex-col w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>
            <CartHeader itemCount={itemCount} />
          </SheetTitle>
        </SheetHeader>
        <CartContent onClose={handleClose} />
      </SheetContent>
    </Sheet>
  );
}
